import React from 'react';
import {
  IoAddOutline,
  IoChevronForward,
  IoGitMergeOutline,
  IoPencilOutline,
  IoReturnUpForwardOutline,
  IoShareOutline,
  IoTrashOutline,
} from 'react-icons/io5';
import FileList from './FileList';
import { FolderCards } from './LibraryHome';
import { folderBadge } from './fileLibrary';

/** One folder: breadcrumb, its actions, its subfolders and the files directly in it. */
export default function FolderView({
  folder,
  folderIndex,
  client,
  refreshKey,
  overrides,
  listProps,
  folderActions,
  onOpenFolder,
  onGoHome,
}) {
  if (!folder) {
    return (
      <div className="fl-empty">
        <strong>This folder is no longer here</strong>
        <span>It may have been deleted, merged or moved by someone else.</span>
        <button type="button" className="fl-link-btn" onClick={onGoHome}>Back to Home</button>
      </div>
    );
  }

  const ancestors = [];
  let parentId = folder.parent_folder_id;
  const seen = new Set();
  while (parentId && folderIndex.byId.has(parentId) && !seen.has(parentId)) {
    seen.add(parentId);
    const parent = folderIndex.byId.get(parentId);
    ancestors.unshift(parent);
    parentId = parent.parent_folder_id;
  }
  const subfolders = folderIndex.children.get(folder.folder_id) || [];
  const badge = folderBadge(folder);

  const header = (
    <div className="fl-folder-head">
      <nav className="fl-breadcrumb" aria-label="Folder path">
        <button type="button" className="fl-link-btn" onClick={onGoHome}>Files</button>
        {ancestors.map((a) => (
          <React.Fragment key={a.folder_id}>
            <IoChevronForward size={12} aria-hidden="true" />
            <button type="button" className="fl-link-btn" onClick={() => onOpenFolder(a.folder_id)}>{a.name}</button>
          </React.Fragment>
        ))}
      </nav>
      <div className="fl-folder-title-row">
        <h3 className="fl-view-title">{folder.name}</h3>
        {badge ? <span className={`fl-badge fl-badge-${badge.toLowerCase()}`}>{badge}</span> : null}
      </div>
      {folder.description ? <p className="fl-view-subtitle">{folder.description}</p> : null}
      <div className="fl-folder-actions" role="group" aria-label="Folder actions">
        <button type="button" className="fl-btn fl-btn-sm" onClick={() => folderActions.createSubfolder(folder)}>
          <IoAddOutline size={14} aria-hidden="true" /> New subfolder
        </button>
        <button type="button" className="fl-btn fl-btn-sm" onClick={() => folderActions.rename(folder)}>
          <IoPencilOutline size={14} aria-hidden="true" /> Rename
        </button>
        <button type="button" className="fl-btn fl-btn-sm" onClick={() => folderActions.move(folder)}>
          <IoReturnUpForwardOutline size={14} aria-hidden="true" /> Move to…
        </button>
        <button type="button" className="fl-btn fl-btn-sm" onClick={() => folderActions.merge(folder)}>
          <IoGitMergeOutline size={14} aria-hidden="true" /> Merge into…
        </button>
        {folderActions.share ? (
          <button type="button" className="fl-btn fl-btn-sm" onClick={() => folderActions.share(folder)}>
            <IoShareOutline size={14} aria-hidden="true" /> Send to company
          </button>
        ) : null}
        <button type="button" className="fl-btn fl-btn-sm fl-btn-danger-ghost" onClick={() => folderActions.remove(folder)}>
          <IoTrashOutline size={14} aria-hidden="true" /> Delete
        </button>
      </div>
      {subfolders.length ? (
        <div className="fl-subfolders">
          <FolderCards folders={subfolders} folderIndex={folderIndex} onOpenFolder={onOpenFolder} />
        </div>
      ) : null}
    </div>
  );

  return (
    <FileList
      {...listProps}
      key={folder.folder_id}
      client={client}
      params={{ folderId: folder.folder_id }}
      refreshKey={refreshKey}
      overrides={overrides}
      headerExtra={header}
      emptyTitle={subfolders.length ? 'No files directly in this folder' : 'This folder is empty'}
      emptyHint="Drag files here from another view, or use Move to… on a file."
    />
  );
}
